// Analytics for the marketing page.
//
// Same Mixpanel project as the app, and deliberately the same SDK: the landing
// sits on vibetoon.com/ and the app on vibetoon.com/app, so localStorage is
// shared and the anonymous distinct_id carries straight over into the builder.
// Hand-rolling the id format here would break that stitching.
//
// The token is a browser-side public key, same as the Stripe publishable key —
// but it must differ per environment, otherwise staging would report into the
// production project. Parcel inlines process.env from the .env file on the
// machine that runs the build, so each server carries its own value.
const TOKEN = process.env.ANALYTICS_TOKEN || '';
const API_HOST = 'https://api-eu.mixpanel.com';

// Campaign attribution. The landing page is the first stop from an ad, so it is
// the one that captures the tag. Storage is shared with the app (same domain);
// key and format match frontend/src/analytics.ts.
var ATTRIBUTION_KEY = 'vt_attribution';
var ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
];

function readAttribution() {
  try {
    var stored = localStorage.getItem(ATTRIBUTION_KEY);
    if (stored) return JSON.parse(stored);

    var query = new URLSearchParams(window.location.search);
    var fresh = {};

    ATTRIBUTION_PARAMS.forEach(function (key) {
      var value = query.get(key);
      if (value) fresh[key] = value.slice(0, 100);
    });

    if (!Object.keys(fresh).length) return {};

    fresh.first_seen_at = new Date().toISOString();
    localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(fresh));
    return fresh;
  } catch (e) {
    return {};
  }
}

var attribution = readAttribution();

let client = null;
let loading = null;
const pending = [];

function load() {
  if (loading) return loading;

  loading = import('mixpanel-browser/dist/mixpanel-core.cjs.js')
    .then((mod) => {
      const instance = mod.default || mod;

      instance.init(TOKEN, {
        api_host: API_HOST,
        persistence: 'localStorage',
        autocapture: false,
        ignore_dnt: false,
        record_sessions_percent: 0,
      });

      client = instance;
      pending.splice(0).forEach((fn) => {
        try {
          fn(instance);
        } catch (e) {
          /* analytics must never break the page */
        }
      });
    })
    .catch(() => {
      pending.length = 0;
    });

  return loading;
}

export function track(event, props) {
  if (!TOKEN) return;

  props = Object.assign({}, attribution, props || {});

  if (client) {
    try {
      client.track(event, props);
    } catch (e) {
      /* ignore */
    }
    return;
  }

  if (pending.length < 20) pending.push((c) => c.track(event, props));
  load();
}

export function init() {
  if (!TOKEN) return;

  // Warm up when the browser is idle — this is a marketing page, nothing here
  // is allowed to compete with the first paint.
  if (window.requestIdleCallback) {
    window.requestIdleCallback(load, { timeout: 5000 });
  } else {
    window.setTimeout(load, 2000);
  }

  track('landing_viewed');

  // Delegated: every route into the app is a CTA, whatever it looks like.
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href^="/app"]');
    if (!link) return;

    track('cta_clicked', {
      cta: link.dataset.cta || 'unknown',
      href: link.getAttribute('href'),
    });
  });
}
